import { NgModule, provideBrowserGlobalErrorListeners, APP_INITIALIZER } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';

import { AppRoutingModule } from './app-routing-module';
import { App } from './app';
import { Header } from './header/header';
import { Footer } from './footer/footer';
import { Upload } from './upload/upload';
import { About } from './about/about';
import { DialogResult } from './dialog-result/dialog-result';

/** Returns the runtime config loaded from /assets/config.json by main.ts. */
function getRuntimeConfig(): Record<string, string> {
  return (window as any).__appConfig ?? {};
}

import {
  IPublicClientApplication,
  PublicClientApplication,
  LogLevel,
  InteractionType
} from '@azure/msal-browser';

import {
  MsalGuard,
  MsalInterceptor,
  MsalBroadcastService,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration
} from '@azure/msal-angular';

@NgModule({
  declarations: [
    App,
    Header,
    Footer,
    Upload,
    About,
    DialogResult
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    HttpClientModule,
    MsalModule
  ],
providers: [
  {
    provide: APP_INITIALIZER,
    useFactory: initializeMsal,
    deps: [MsalService],
    multi: true
  },
  // { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
  {
    provide: MSAL_INSTANCE,
    useFactory: MSALInstanceFactory
  },
  {
    provide: MSAL_GUARD_CONFIG,
    useFactory: MSALGuardConfigFactory
  },
  {
    provide: MSAL_INTERCEPTOR_CONFIG,
    useFactory: MSALInterceptorConfigFactory
  },
  MsalService,
  MsalGuard,
  MsalBroadcastService,
  provideBrowserGlobalErrorListeners()
],
  bootstrap: [App]
})
export class AppModule { }

/**
 * MSAL Instance Factory
 *
 * Reads clientId, tenantId, and redirectUri from window.__appConfig, which
 * main.ts populates from /assets/config.json before Angular bootstraps.
 * Ansible writes env-specific values from env-variables.yml into that file
 * at deploy time (via azure_app_service_angular_app_settings).
 */
export function MSALInstanceFactory(): IPublicClientApplication {
  const config = getRuntimeConfig();
  return new PublicClientApplication({
    auth: {
      clientId: config['clientId'],
      authority: `https://login.microsoftonline.com/${config['tenantId']}`,
      redirectUri: config['redirectUri'],
      postLogoutRedirectUri: config['redirectUri'],
      navigateToLoginRequestUrl: true
    },
    cache: {
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false
    },
    system: {
      allowNativeBroker: false,
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) return;
          console.log(message);
        },
        logLevel: LogLevel.Warning,
        piiLoggingEnabled: false
      }
    }
  });
}

/**
 * MSAL Interceptor Configuration Factory
 */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  
  // Add your API endpoint and required scopes
  protectedResourceMap.set('http://localhost:8000/*', ['user.read']);
  protectedResourceMap.set('https://wtv-api-dev.azure.chevron.com/*', ['user.read']);
  
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

/**
 * MSAL Initialization - Simple initialization without retry logic
 */
export function initializeMsal(msalService: MsalService) {
  return () => {
    // Initialize MSAL and handle redirect
    return msalService.instance.initialize().then(() => {
      return msalService.instance.handleRedirectPromise().then((result) => {
        if (result) {
          console.log('Redirect result:', result);
          msalService.instance.setActiveAccount(result.account);
        }
      }).catch((error) => {
        console.error('Error handling redirect promise:', error);
        // Error will be visible without any automatic retry or cache clearing
        throw error;
      });
    });
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read']
    }
  };
}